import { useState } from "react";
import { ArrowLeft, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ExerciseSearchCombobox from "./ExerciseSearchCombobox";
import { adminExerciseUrl } from "./utils";
import type { WorkoutGeneratorLine } from "@/integrations/backend/workout-generator";

type Props = {
  line: WorkoutGeneratorLine;
  busy: boolean;
  onInclude: () => Promise<void>;
  onExclude: () => Promise<void>;
  onUndoInclude: () => Promise<void>;
  onAddExercise: (exerciseId: string) => Promise<void>;
  onRemoveExercise: (mappingId: string) => Promise<void>;
  onFinalize: () => Promise<void>;
  onReopen: () => Promise<void>;
  onAiGenerate: () => Promise<void>;
};

function ExerciseChip({
  name,
  aiGenerated,
  mappingId,
  onRemove,
  busy,
}: {
  name: string;
  aiGenerated?: boolean;
  mappingId: string;
  onRemove: () => void | Promise<void>;
  busy: boolean;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs shadow-sm">
      <a
        href={adminExerciseUrl(name)}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 truncate font-medium text-gray-900 underline-offset-2 hover:text-blue-800 hover:underline"
      >
        {name}
      </a>
      {aiGenerated ? (
        <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800">
          AI
        </span>
      ) : null}
      <button
        type="button"
        className="-mr-0.5 shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:opacity-50"
        disabled={busy}
        aria-label={`Remove ${name}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void onRemove();
        }}
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

function BackControl({ busy, onClick }: { busy: boolean; onClick: () => void | Promise<void> }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="-ml-2 h-7 w-7 shrink-0 p-0 text-gray-600 hover:text-gray-900"
      disabled={busy}
      aria-label="Back"
      onClick={() => void onClick()}
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
    </Button>
  );
}

export default function ExerciseLineRow({
  line,
  busy,
  onInclude,
  onExclude,
  onUndoInclude,
  onAddExercise,
  onRemoveExercise,
  onFinalize,
  onReopen,
  onAiGenerate,
}: Props) {
  const [resolving, setResolving] = useState(false);

  const handleRemoveFromFinalized = async (mappingId: string) => {
    await onReopen();
    await onRemoveExercise(mappingId);
  };

  if (line.name_status === "excluded") {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-sm text-gray-500">
        <BackControl busy={busy} onClick={onUndoInclude} />
        <span className="line-through">{line.parsed_name}</span>
        <span className="text-xs">Excluded</span>
      </div>
    );
  }

  if (line.name_status === "pending") {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 text-sm">
        <span className="font-medium text-gray-900">{line.parsed_name}</span>
        <Button
          type="button"
          size="sm"
          variant="default"
          disabled={busy}
          onClick={() => {
            setResolving(true);
            void onInclude().finally(() => setResolving(false));
          }}
        >
          {resolving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Include exercise"}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void onExclude()}>
          Exclude
        </Button>
      </div>
    );
  }

  const isFinalized = line.binding_status === "finalized";
  const mappedExerciseIds = line.exercises.map((ex) => ex.id);

  if (isFinalized) {
    return (
      <div className="space-y-2 rounded border border-green-100 bg-green-50 px-2 py-2 text-sm">
        <div className="font-medium text-gray-900">{line.parsed_name}</div>
        <div className="flex flex-wrap gap-1.5">
          {line.exercises.map((ex) => (
            <ExerciseChip
              key={ex.mapping_id}
              name={ex.name}
              aiGenerated={ex.ai_generated}
              mappingId={ex.mapping_id}
              busy={busy}
              onRemove={() => handleRemoveFromFinalized(ex.mapping_id)}
            />
          ))}
        </div>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void onReopen()}>
          Edit pool
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded border border-gray-200 bg-white px-2 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <BackControl busy={busy} onClick={onUndoInclude} />
        <span className="font-medium text-gray-900">{line.parsed_name}</span>
      </div>
      {line.exercises.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {line.exercises.map((ex) => (
            <ExerciseChip
              key={ex.mapping_id}
              name={ex.name}
              aiGenerated={ex.ai_generated}
              mappingId={ex.mapping_id}
              busy={busy}
              onRemove={() => onRemoveExercise(ex.mapping_id)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500">No exercises mapped yet. Add from search or create with AI.</p>
      )}
      <ExerciseSearchCombobox
        placeholder={`Search exercises (e.g. ${line.parsed_name})…`}
        mappedExerciseIds={mappedExerciseIds}
        disabled={busy}
        onAddExercise={(ex) => void onAddExercise(ex.id)}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Not in DB yet?{" "}
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-violet-700 underline-offset-2 hover:text-violet-900 hover:underline disabled:pointer-events-none disabled:opacity-50"
            disabled={busy}
            onClick={() => void onAiGenerate()}
          >
            {busy ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                Creating…
              </>
            ) : (
              "Create with AI"
            )}
          </button>
        </p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={busy || line.exercises.length === 0}
            onClick={() => void onFinalize()}
          >
            Finalize
          </Button>
        </div>
      </div>
    </div>
  );
}
